import React from 'react'
import { Box, useTheme } from '@mui/material'
import { contrastRatio, readableTextOn } from 'utils/contrast'

/**
 * The applied-count indicator on a toolbar control — an INLINE pill that sits
 * inside the button, after its label.
 *
 * ## Why it is inline, and why that changed
 *
 * This was a corner OVERLAY: an 18px circle absolutely positioned at
 * `top:-4 right:-4`, straddling the button's edge. That is not what the
 * platform does, and the difference was structural rather than decorative, so
 * it is the half of the "sort looks wrong" report that a colour tweak could
 * never have fixed.
 *
 * `apps/platform` renders the count INSIDE the button in BOTH of its toolbar
 * controls, from two independent call sites that agree exactly —
 * `components/input/Sort/Sort.tsx` and `components/input/filters/Filters.tsx`
 * — as a 22px round pill with `marginLeft: 3` (12px on the 4px base) after the
 * label. Measured live on the platform's Certificates page: 22 x 22, radius
 * round, `margin: 4px 4px 4px 12px`, 12px/400 text. Those are the numbers
 * reproduced here.
 *
 * The overlay ALSO could not survive the other half of the fix. The clear-sort
 * affordance is now ATTACHED to the sort button as a segmented half (see
 * `ToolbarControl`), and a badge anchored to the group's top-right corner
 * lands on the CLEAR half — labelling the wrong thing — while a badge anchored
 * to the sort half alone is painted over by the segment overlapping it. The
 * old file's own comment records that exact collision being measured at 1280px
 * (badge 1158-1176 under a clear segment at 1164-1208). Inline has no corner to
 * collide over, which is presumably why the platform chose it.
 *
 * ## Colour: the tenant's brand, or Vera's gradient when there is no tenant
 *
 * Two fills, chosen by ONE question — did a brand supply a colour?
 *
 * | | fill | why |
 * |---|---|---|
 * | tenant brand | `palette.brand[600]` | this app renders inside a BRAND's own site, so a fixed Vera purple would be an alien mark on someone else's page |
 * | base Vera theme (`app.vera.inc`) | `VERA_COUNT_GRADIENT` | Vera purple is not a "fallback", it is the base theme's own accent — the same framing `settings/mui/palette.ts` records for `primary.main` |
 *
 * The gradient is the platform's, verbatim and measured from it:
 * `apps/platform/src/components/input/Sort/Sort.tsx:412` and
 * `input/filters/Filters.tsx:381` both paint
 * `linear-gradient(90deg, rgba(130,90,195,1) 0%, rgb(55,39,137) 100%)`.
 *
 * **The signal is `palette.brand.isTenantDerived`** (`settings/mui/brandRamp.ts`),
 * which is not a new check — it is the ramp's own existing
 * `(source && parseHex(source)) || [0,0,0]` branch, named. This replaces the
 * BLACK the neutral ramp produced on an unthemed tenant: measured
 * `rgb(0, 0, 0)`, a solid black disc, which represented nobody. A brand that
 * deliberately picks `#000000` still gets black, because that IS its brand.
 *
 * ## The text colour is COMPUTED for both fills, never hardcoded
 *
 * A tenant fill spans the whole gamut, so white and black each become
 * unreadable on some brand — the foreground is the higher-contrast of the two
 * by WCAG relative luminance (`utils/contrast`).
 *
 * The gradient needs one extra step, because it has no single background: a
 * foreground legible on `#825AC3` can be illegible on `#372789`. So the
 * candidate is chosen by its WORST contrast across both stops (a max-min), not
 * by an average or an endpoint. It resolves to WHITE — 5.00:1 on the light stop
 * and 11.58:1 on the dark one, against black's 4.20:1 / 1.81:1. Black loses on
 * the dark stop by a factor of six, which is exactly the failure an endpoint-only
 * check would miss.
 *
 * Note the candidates are `palette.common.white` / `.black`:
 * `theme.palette.white` is a `@repo/ui` key that this app's themes do not define.
 *
 * ## It pops in, and only when it APPEARS
 *
 * See `POP_KEYFRAMES` below. The distinction that costs the most to get wrong is
 * that a pill *appearing* (0 -> N) and a pill *changing* (1 -> 2) are different
 * events and get different motion, and that ARRIVING on a page which already has
 * a sort applied is neither — nothing just happened, so nothing animates.
 *
 * ## Accessibility
 *
 * The pill is `aria-hidden`: it is a visual echo of a number that belongs in
 * the CONTROL's accessible name, and announcing it twice is worse than not
 * announcing it at all. Callers pass `countedLabel('Filter', n)` as the
 * control's `aria-label` so a screen reader hears "Filter, 2 active".
 */
export interface CountPillProps {
  /** Zero or negative renders nothing at all — no element, no layout change. */
  count: number
  /** Counts above this render as `${max}+`. */
  max?: number
}

/**
 * The accessible name for a counted control: `"Filter"` -> `"Filter, 2 active"`.
 *
 * Lives beside the pill so the visual count and the spoken count cannot drift
 * apart, and so every call site phrases it identically.
 */
export const countedLabel = (label: string, count: number): string =>
  count > 0 ? `${label}, ${count} active` : label

/** Diameter of the pill; also its min-width, so `1` renders as a circle. */
const PILL_SIZE = 22

/**
 * The base Vera theme's pill fill, as two stops rather than a CSS string, so
 * the contrast computation can see both. Values are the platform's own —
 * `rgba(130,90,195,1)` and `rgb(55,39,137)` — written as hex because
 * `utils/contrast` parses both and hex is the form the rest of this app's
 * tokens take.
 */
export const VERA_COUNT_GRADIENT_STOPS = ['#825AC3', '#372789'] as const

const VERA_COUNT_GRADIENT = `linear-gradient(90deg, ${VERA_COUNT_GRADIENT_STOPS[0]} 0%, ${VERA_COUNT_GRADIENT_STOPS[1]} 100%)`

/**
 * The higher-contrast foreground across EVERY stop of a gradient — the
 * candidate whose WORST contrast is best (a max-min).
 *
 * `readableTextOn` answers this for one background. A gradient has no single
 * background, and the naive reductions both fail: an endpoint-only check reads
 * whichever stop you happened to pick, and an averaged colour can be legible
 * while neither real stop is. Taking the minimum makes the guarantee the one
 * that actually matters — the number is legible everywhere on the pill, not on
 * average.
 */
const readableTextOnStops = (
  stops: readonly string[],
  options: { light: string; dark: string },
): string => {
  const worst = (foreground: string) =>
    stops.reduce((lowest, stop) => {
      const ratio = contrastRatio(stop, foreground)
      // An unparseable stop contributes nothing rather than poisoning the
      // result — same failure posture as `utils/contrast` itself.
      return ratio === null ? lowest : Math.min(lowest, ratio)
    }, Number.POSITIVE_INFINITY)

  const againstLight = worst(options.light)
  const againstDark = worst(options.dark)
  // Neither parseable: fall back the way `readableTextOn` does, to light.
  if (!Number.isFinite(againstLight) && !Number.isFinite(againstDark)) {
    return options.light
  }
  return againstLight >= againstDark ? options.light : options.dark
}

/**
 * APPEARING: 0 -> N. Scale overshoot plus opacity, so the pill arrives with
 * some weight instead of being switched on.
 *
 * `0.6 -> 1.08 -> 1`, with the overshoot at 55% of the timeline. The overshoot
 * is what makes it read as a *pop* rather than a zoom; 1.08 on a 22px disc is
 * under 2px of travel, which is enough to register and small enough not to
 * collide with the button edge it sits inside.
 *
 * ## The easing is PER-KEYFRAME, and that is not a stylistic preference
 *
 * These offsets only mean what they say if the effect's own easing is `linear`.
 * This first shipped sharing the clear-X's `cubic-bezier(0.32, 0.72, 0, 1)` at
 * the effect level, on the "one gesture, one curve" argument. Scrubbing the
 * animation frame by frame killed that: the curve is a page-slide curve and is
 * violently front-loaded, so it reached ~72% progress by t=55ms and the pill
 * measured `scale(1.039)` at 55ms and `scale(1.008)` at 110ms. The 1.08 peak
 * was landing near t=33ms and the remaining ~187ms was an imperceptible drift
 * from 1.008 to 1.000. Nominally a 220ms pop; in practice a ~60ms flicker
 * followed by nothing.
 *
 * So the pacing lives on the keyframes and the effect easing is `linear`. The
 * peak now falls where the offset says it does, at 0.55 x 220 = 121ms.
 *
 * The two elements therefore share a DURATION and not a curve, which is the
 * honest version of "one gesture": they start and finish together, but a
 * translate wants a glide and a scale overshoot wants its own timing.
 */
const POP_KEYFRAMES: Keyframe[] = [
  { transform: 'scale(0.6)', opacity: 0, easing: 'cubic-bezier(0.2, 0, 0.2, 1)' },
  { transform: 'scale(1.08)', opacity: 1, offset: 0.55, easing: 'cubic-bezier(0.4, 0, 0.4, 1)' },
  { transform: 'scale(1)', opacity: 1 },
]

/**
 * CHANGING: N -> M, both non-zero. A different event, so a deliberately weaker
 * treatment — no opacity, no growth from nothing, just a bump.
 *
 * It animates at all because the pill does not MOVE when its digit changes: a
 * `1` becoming a `2` inside a 22px disc, while the user's attention is on the
 * sort sheet they are interacting with, is genuinely easy to miss. It is weaker
 * than the pop because conflating "a count appeared" with "a count changed"
 * would spend the same signal on both and let neither mean anything.
 *
 * The alternative considered was no motion at all (what the platform does). It
 * loses on the missed-change argument above; if this ever reads as fussy, this
 * is the branch to delete, not the pop.
 */
const BUMP_KEYFRAMES: Keyframe[] = [
  { transform: 'scale(1)', easing: 'cubic-bezier(0.2, 0, 0.2, 1)' },
  { transform: 'scale(1.15)', offset: 0.4, easing: 'cubic-bezier(0.4, 0, 0.4, 1)' },
  { transform: 'scale(1)' },
]

/**
 * 220ms, mid-range of the 200-260 the pop idiom wants, and the SAME DURATION as
 * the clear-X slide in `ToolbarControl`. Applying a sort fires both at once, so
 * they start and finish together rather than reading as two separate events.
 *
 * The CURVE is not shared — see `POP_KEYFRAMES` for the measurement that
 * settled it.
 */
const POP_MS = 220
/** Shorter, because a bump has less distance to cover and no arrival to sell. */
const BUMP_MS = 160
/**
 * `linear` at the EFFECT level so the keyframe offsets are honoured literally;
 * all pacing is on the keyframes themselves.
 */
const POP_EASING = 'linear'

/**
 * Reduced motion is checked in JS, not CSS, and that is not a style preference.
 *
 * A Web Animations `element.animate()` call is invisible to a media query — the
 * same trap MUI's `Fade`/`Grow` set with inline transitions, hit earlier in this
 * app's `ContentFade`. The only way to honour the setting is to not start the
 * animation. Since the keyframes end at the element's natural state and nothing
 * sets a pre-animation style, skipping leaves the pill correctly rendered with
 * no cleanup — the finished state IS the default state.
 */
const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches

export const CountPill: React.FC<CountPillProps> = ({ count, max = 99 }) => {
  const theme = useTheme()
  const pillRef = React.useRef<HTMLDivElement | null>(null)

  const visible = Number.isFinite(count) && count > 0 ? count : 0

  /**
   * The PREVIOUS count, or `null` on the very first render.
   *
   * `null` is the whole mechanism for "do not pop on mount". Arriving on a page
   * that already has a sort applied is not an event — nothing just happened —
   * so the first pass only records the count and animates nothing. A ref rather
   * than state because reading it must not itself cause a render.
   */
  const previous = React.useRef<number | null>(null)

  // Layout effect, not `useEffect`: the animation must start in the same frame
  // the element is painted, or the pill is briefly visible at full size before
  // it scales down to begin.
  React.useLayoutEffect(() => {
    const before = previous.current
    previous.current = visible

    if (before === null) return // mount — see `previous`
    if (visible === before) return // a re-render that changed nothing
    if (visible === 0) return // going away: the element is already unmounted
    if (prefersReducedMotion()) return

    const node = pillRef.current
    if (!node || typeof node.animate !== 'function') return

    const appearing = before === 0
    const animation = node.animate(appearing ? POP_KEYFRAMES : BUMP_KEYFRAMES, {
      duration: appearing ? POP_MS : BUMP_MS,
      easing: POP_EASING,
    })
    // Named so it is identifiable in `getAnimations()` — this app renders in a
    // shadow root, where `document.getAnimations()` returns nothing and the
    // only way to inspect motion is `shadowRoot.getAnimations()`.
    animation.id = appearing ? 'count-pill-pop' : 'count-pill-bump'
  }, [visible])

  // Nothing to say. Render nothing rather than an empty element — an extra
  // inline-flex child inside the button is a real layout difference.
  //
  // NOTE this sits BELOW the hooks deliberately: the effect above must run on
  // the renders where the count is zero too, or `previous` would never learn
  // that the pill went away and the next appearance would not pop.
  if (visible === 0) return null

  const brand = (theme.palette as any).brand
  const tenantDerived = Boolean(brand?.isTenantDerived)
  const fill = brand?.[600] || theme.palette.primary?.main || '#000000'

  const light = theme.palette.common?.white || '#FFFFFF'
  const dark = theme.palette.common?.black || '#000000'
  const color = tenantDerived
    ? readableTextOn(fill, { light, dark })
    : readableTextOnStops(VERA_COUNT_GRADIENT_STOPS, { light, dark })

  return (
    <Box
      ref={pillRef}
      aria-hidden
      sx={{
        // ABSOLUTE px, not spacing factors. `apps/app` is on MUI's default 8px
        // base while `@repo/ui`/platform are on 4px (working-conventions §29),
        // so the platform's `margin: 1` / `marginLeft: 3` would render at
        // DOUBLE size if copied as factors. 4px and 12px are the platform's
        // rendered values, measured.
        margin: '4px 4px 4px 12px',
        minWidth: `${PILL_SIZE}px`,
        height: `${PILL_SIZE}px`,
        // Horizontal padding only — the height is fixed, so a two- or
        // three-character count grows the pill sideways into a lozenge rather
        // than changing the button's vertical rhythm.
        padding: '0 6px',
        boxSizing: 'border-box',
        borderRadius: `${PILL_SIZE / 2}px`,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        // Tenant: a flat brand fill. Base theme: Vera's gradient. `background`
        // (the shorthand) rather than `backgroundColor` + `backgroundImage`,
        // so switching between the two cannot leave a stale layer behind.
        background: tenantDerived ? fill : VERA_COUNT_GRADIENT,
        color,
        fontSize: '12px',
        fontWeight: 400,
        // 1, so a 12px glyph is centred in a 22px circle by the flexbox rather
        // than by inherited leading (which differs per brand font and would
        // drift the number off-centre).
        lineHeight: 1,
        // Digits are the entire content; proportional figures make a count
        // visibly shift its centre as it goes 1 -> 2 -> 3.
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {count > max ? `${max}+` : count}
    </Box>
  )
}

export default CountPill
